import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const MAX_RESTORE_PAGES = 15;
const RESTORE_FETCH_BATCH = 3;
const RESTORE_TIMEOUT_MS = 8000;
const PAGE_FETCH_TIMEOUT_MS = 15000;

const SESSION_KEY = 'paginated-list-window';

/** @param {number} ms */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Identifies a list by its path and filters/sort, ignoring which page is in the URL. */
function getListKey() {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  return url.pathname + url.search;
}

/** @returns {{ plMin?: number, plMax?: number, scrollTop?: number } | null} */
function readSessionWindow() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null');
    return saved && saved.key === getListKey() ? saved : null;
  } catch (_) {
    return null;
  }
}

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  #restoring = false;
  #renderingNext = false;
  #renderingPrevious = false;
  #recheckAfterRender = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Must run before the observer starts so early sentinel hits are ignored while restoring
    this.#restorePageWindow();

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    window.addEventListener('pagehide', this.#savePageWindow);

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    window.removeEventListener('pagehide', this.#savePageWindow);
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  // Re-observing makes the observer report the current state again, so a trigger that was dropped mid-render is not lost
  #recheckSentinels() {
    const observer = this.infinityScrollObserver;
    if (!observer) return;

    const { viewMorePrevious, viewMoreNext } = this.refs;
    for (const element of [viewMorePrevious, viewMoreNext]) {
      if (!element) continue;
      observer.unobserve(element);
      observer.observe(element);
    }
  }

  /**
   * @param {HTMLElement} grid
   * @returns {HTMLElement[]}
   */
  #getCards(grid) {
    return /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(':scope > [ref="cards[]"]')]);
  }

  /**
   * @param {HTMLElement} grid
   * @param {number} page
   * @returns {boolean}
   */
  #hasPageInGrid(grid, page) {
    return grid.querySelector(`:scope > [ref="cards[]"][data-page="${page}"]`) !== null;
  }

  /**
   * Records which pages are loaded in the history entry, so a back/reload can rebuild the same list.
   */
  #savePageWindow = () => {
    if (this.#restoring) return;

    const { grid } = this.refs;
    if (!grid) return;

    const pages = this.#getCards(grid)
      .map((card) => Number(card.dataset.page))
      .filter(Number.isInteger);
    if (pages.length === 0) return;

    // History entry state does not reliably survive a reload, so a copy lives in sessionStorage
    try {
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ key: getListKey(), plMin: Math.min(...pages), plMax: Math.max(...pages), scrollTop: getScrollTop() })
      );
    } catch (_) {
      // Storage can be unavailable or full
    }

    try {
      const currentState = typeof history.state === 'object' && history.state !== null ? history.state : {};
      history.replaceState({ ...currentState, plMin: Math.min(...pages), plMax: Math.max(...pages) }, '');
    } catch (_) {
      // replaceState can throw if the state object exceeds the browser's size limit
    }
  };

  /**
   * On back/forward or reload only the page in the URL is server-rendered. Re-fetch the other pages that were
   * loaded before leaving, then restore the saved scroll position, so the list looks exactly as it did.
   */
  #restorePageWindow() {
    const navType = /** @type {PerformanceNavigationTiming | undefined} */ (
      performance.getEntriesByType('navigation')[0]
    )?.type;

    const state = navType === 'back_forward' ? history.state : navType === 'reload' ? readSessionWindow() : null;
    if (!state || typeof state !== 'object') return;

    const { plMin, plMax, scrollTop } = state;
    const { grid } = this.refs;
    if (!grid || !Number.isInteger(plMin) || !Number.isInteger(plMax)) return;

    const currentPage = Number(this.#getCards(grid)[0]?.dataset.page);
    const lastPage = Number(grid.dataset.lastPage);
    if (!Number.isInteger(currentPage) || !Number.isInteger(lastPage)) return;

    const firstPage = Math.max(1, Math.min(plMin, currentPage));
    const finalPage = Math.min(lastPage, Math.max(plMax, currentPage));
    const alreadyComplete = firstPage === currentPage && finalPage === currentPage;
    if (alreadyComplete || finalPage - firstPage + 1 > MAX_RESTORE_PAGES) return;

    this.#restoring = true;
    grid.style.visibility = 'hidden';

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      grid.style.visibility = '';
      this.#restoring = false;
      this.#recheckSentinels();
    };
    const timer = setTimeout(finish, RESTORE_TIMEOUT_MS);

    (async () => {
      try {
        /** @type {number[]} */
        const missing = [];
        for (let page = firstPage; page <= finalPage; page++) {
          if (page !== currentPage) missing.push(page);
        }

        for (let i = 0; i < missing.length; i += RESTORE_FETCH_BATCH) {
          await Promise.all(
            missing
              .slice(i, i + RESTORE_FETCH_BATCH)
              .map((page) => (this.pages.has(page) ? undefined : this.#fetchSpecificPage(page).catch(() => {})))
          );
        }

        /** @type {Element[]} */
        const before = [];
        /** @type {Element[]} */
        const after = [];
        for (const page of missing) {
          const cards = this.#getGridForPage(page);
          if (!cards || cards.length === 0) return;
          (page < currentPage ? before : after).push(...cards);
        }

        if (finished || !this.isConnected || Number(this.#getCards(grid)[0]?.dataset.page) !== currentPage) return;

        grid.prepend(...before);
        grid.append(...after);
        this.#aspectRatioHelper?.processNewElements();
        // The base component's mutation observer attaches on idle, so refs.cards must be refreshed explicitly
        this.updatedCallback();
        for (const page of missing) this.pages.delete(page);

        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (Number.isFinite(scrollTop)) scrollTo({ top: scrollTop, behavior: 'instant' });
      } finally {
        clearTimeout(timer);
        finish();
      }
    })();
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (_) {
      // Leave the page uncached so the next trigger retries
    }
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * Updates the address bar without adding a history entry per auto-loaded page.
   * @param {URL} url
   */
  #replaceUrl(url) {
    history.replaceState(history.state, '', url.toString());
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid || this.#restoring) return;
    if (this.#renderingNext) {
      this.#recheckAfterRender = true;
      return;
    }

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    if (this.#hasPageInGrid(grid, nextPage.page)) return;

    this.#renderingNext = true;
    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await Promise.race([promise, sleep(PAGE_FETCH_TIMEOUT_MS)]);
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      if (this.#restoring || this.#hasPageInGrid(grid, nextPage.page)) return;

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper.processNewElements();

      await yieldToMainThread();

      this.#replaceUrl(nextPage.url);

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#renderingNext = false;
      this.#flushRecheck();
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid || this.#restoring) return;
    if (this.#renderingPrevious) {
      this.#recheckAfterRender = true;
      return;
    }

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;
    if (this.#hasPageInGrid(grid, previousPage.page)) return;

    this.#renderingPrevious = true;
    try {
      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await Promise.race([promise, sleep(PAGE_FETCH_TIMEOUT_MS)]);
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      if (this.#restoring || this.#hasPageInGrid(grid, previousPage.page)) return;

      // Store the current scroll position and height of the first element
      const currentScrollTop = getScrollTop();
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper.processNewElements();

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
        const heightDiff = newHeight - oldHeight;
        scrollTo({
          top: currentScrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      await yieldToMainThread();

      this.#replaceUrl(previousPage.url);

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#renderingPrevious = false;
      this.#flushRecheck();
    }
  }

  #flushRecheck() {
    if (!this.#recheckAfterRender || this.#renderingNext || this.#renderingPrevious) return;
    this.#recheckAfterRender = false;
    requestAnimationFrame(() => this.#recheckSentinels());
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
